import React, { useEffect, useState } from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Link, Stack, TextField, Typography, useMediaQuery } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import _ from "lodash";
import pluginServices from "app/services/plugin-services";
import { API_HOST } from "app/utils/constants/paths";
import { isValidPhone, normalizePhoneInput } from "app/utils/phone";
import { pluginErrorMessage } from "./pluginHelpers";

const API_BASE_URL = (API_HOST || "").replace(/\/api\/v1\/?$/, "");

const resolvePluginHelpUrl = (helpUrl) => {
    if (!helpUrl) return "";
    if (/^https?:\/\//i.test(helpUrl)) return helpUrl;
    const normalizedPath = helpUrl.startsWith("/") ? helpUrl : `/${helpUrl}`;
    return `${API_BASE_URL}${normalizedPath}`;
};

const isPhoneField = (fieldKey = "") => {
    const normalizedKey = `${fieldKey}`.toLowerCase();
    if (normalizedKey.includes("sid")) return false;
    return normalizedKey.includes("phone") || normalizedKey.includes("whatsapp");
};

// Un campo de la configuración. Las claves (type password) nunca llegan del servidor: si ya hay una guardada, se dice
// y queda en blanco para no cambiarla.
const ConfigField = ({ field, fieldKey, value, secretSet, onChange }) => {
    if (field.type !== "string" && field.type !== "password") return null;

    const url = resolvePluginHelpUrl(field.help_url);
    const phoneField = isPhoneField(fieldKey);
    const requiresPlus = fieldKey === "twilio_whatsapp_from";
    const invalidPhone = phoneField && value && !isValidPhone(value, { requirePlus: requiresPlus });
    const fallbackExpectedFormat = requiresPlus ? "Formato esperado: +14155238886" : "Formato esperado: +56912345678 o 56912345678";
    const helperBaseText = _.compact([
        secretSet ? "Guardada: déjala en blanco para no cambiarla." : null,
        invalidPhone ? (field.help || fallbackExpectedFormat) : field.help,
    ]).join(" ");

    return (
        <TextField
            fullWidth
            type={field.type === "password" ? "password" : "text"}
            required={field.required && !secretSet}
            label={field.label}
            value={value || ""}
            error={invalidPhone}
            placeholder={secretSet ? "••••••••" : undefined}
            autoComplete={field.type === "password" ? "new-password" : "off"}
            onChange={(event) => onChange(phoneField ? normalizePhoneInput(event.target.value, { forcePlus: requiresPlus }) : event.target.value)}
            helperText={(
                <>
                    {helperBaseText}
                    {!!url && (
                        <>
                            {helperBaseText ? " " : ""}
                            <Link href={url} target="_blank" rel="noopener noreferrer" underline="hover">{field.help_label || "Ver instrucciones"}</Link>
                        </>
                    )}
                </>
            )}
        />
    );
};

// Configurar un plugin instalado en un diálogo. Si está inactivo, se puede guardar y activar de una vez.
const PluginConfigDialog = ({ plugin, notice, onClose, onSaved }) => {
    const [values, setValues] = useState({});
    const [saving, setSaving] = useState(null);
    const [error, setError] = useState(null);
    const fullScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

    useEffect(() => {
        setValues({ ...(plugin?.data_values || {}) });
        setError(null);
        setSaving(null);
    }, [plugin]);

    if (!plugin) return null;

    const fields = plugin.data_fields || {};
    const secretsSet = plugin.secrets_set || [];
    const missingRequired = _.some(fields, (field, key) => field.required && !`${values[key] || ""}`.trim() && !secretsSet.includes(key));

    const save = async (activate) => {
        setSaving(activate ? "activate" : "save");
        setError(null);
        try {
            await pluginServices.saveConfig(plugin.id, { data_values: values });
        } catch (requestError) {
            setError(pluginErrorMessage(requestError, "No se pudo guardar la configuración."));
            setSaving(null);
            return;
        }
        try {
            if (activate) await pluginServices.togglePluginActivation(plugin.id);
            onSaved(plugin, activate);
        } catch (requestError) {
            setError(`La configuración quedó guardada, pero no se pudo activar: ${pluginErrorMessage(requestError, "error desconocido")}`);
            setSaving(null);
        }
    };

    const canActivate = plugin.installed && !plugin.enabled;

    return (
        <Dialog open onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
            <DialogTitle>Configurar «{plugin.name}»</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    {!!notice && <Alert severity="warning">{notice}</Alert>}
                    {_.isEmpty(fields) && <Typography variant="body2">Este plugin no tiene parámetros que configurar.</Typography>}
                    {_.map(fields, (field, key) => (
                        <ConfigField
                            key={key}
                            field={field}
                            fieldKey={key}
                            value={values[key]}
                            secretSet={secretsSet.includes(key)}
                            onChange={(value) => setValues((current) => ({ ...current, [key]: value }))}
                        />
                    ))}
                    {!!error && <Alert severity="error">{error}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1.5 }}>
                <Button onClick={onClose} disabled={!!saving}>Cancelar</Button>
                <LoadingButton variant={canActivate ? "outlined" : "contained"} loading={saving === "save"} disabled={!!saving} onClick={() => save(false)}>
                    Guardar
                </LoadingButton>
                {canActivate && (
                    <LoadingButton variant="contained" loading={saving === "activate"} disabled={!!saving || missingRequired} onClick={() => save(true)}>
                        Guardar y activar
                    </LoadingButton>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default PluginConfigDialog;
